<script setup lang="ts">
import { computed } from 'vue'
import { equipIcon, equipTypeList } from '@/views/item/data'
import { equipCollection } from '@/core/equip'
import { getEquipName } from '@/utils/equip'
import type { OtherUserInfoType } from '@/types/fight'
import EquipInfo from '@/views/item/components/EquipInfo.vue'
import { dayjs } from 'element-plus'
import { ref } from 'vue'
import { getRuneName } from '@/core/rune'
import RuneInfo from '@/views/item/components/RuneInfo.vue'
import { getRelicName } from '@/core/relic'
import RelicInfo from '@/views/item/components/RelicInfo.vue'
import { getSkillName } from '@/core/skill'
import SkillInfo from '@/views/item/components/SkillInfo.vue'
import { formatNumber } from '@/utils'

const props = defineProps<{ userInfo: OtherUserInfoType }>()

const leftShowType = ref<'info' | 'attr'>('info')
const showType = ref('equip')
function getRealSpecAttr(attr: number) {
  return (attr / (attr + 100)) * 100
}
const equipInfoList = computed(() => {
  if (!props.userInfo.equipList) return undefined
  const res = props.userInfo.equipList?.map((item) => {
    if (!item) return undefined
    const { name, type, attrs } = equipCollection[item.equipId]
    return { ...item, name, type, attrs }
  })
  const typeOrder =
    (props.userInfo.equipList?.length || 0) > 5
      ? ['weapon', 'helmet', 'armor', 'belt', 'glove', 'pants', 'shoes', 'necklace', 'jewelry', 'medal']
      : ['weapon', 'helmet', 'armor', 'shoes', 'jewelry']
  return typeOrder.map((type) => {
    return res?.find((item) => item?.type === type)
  })
})
</script>

<template>
  <div class="other-user-info">
    <h3>玩家信息</h3>
    <div class="info">
      <div class="player-info border-wrap">
        <h4>
          <span :style="{ opacity: leftShowType === 'info' ? '1' : '0.5' }" @click="leftShowType = 'info'">信息</span>
          <span> | </span>
          <span :style="{ opacity: leftShowType === 'attr' ? '1' : '0.5' }" @click="leftShowType = 'attr'">属性</span>
        </h4>
        <div v-show="leftShowType === 'info'">
          <p><span>用户ID：</span>{{ userInfo.userId || '(已隐藏)' }}</p>
          <p>
            <span>昵称：</span><span :style="{ color: userInfo.nicknameColor }">{{ userInfo.nickname }}</span>
          </p>
          <p>
            <span>会员等级：</span><span>{{ typeof userInfo.vipLevel === 'number' ? userInfo.vipLevel : '(已隐藏)' }}</span>
          </p>
          <p>
            <span>注册时间：</span><span>{{ userInfo.created ? dayjs(userInfo.created).format('YYYY-MM-DD') : '(已隐藏)' }}</span>
          </p>
        </div>
        <div class="attr-info" v-show="leftShowType === 'attr'">
          <p style="text-align: center" v-if="!userInfo.userAttr">（已隐藏）</p>
          <template v-else>
            <div class="text-wrap">
              <p>
                <span>攻击：</span><span>{{ userInfo.userAttr.atk }}</span>
              </p>
              <p>
                <span>攻击速度：</span><span>{{ (userInfo.userAttr.atksp / 100).toFixed(3) }}</span>
              </p>
              <p>
                <span>暴击率：</span><span>{{ userInfo.userAttr.crt }}%</span>
              </p>
              <p>
                <span>暴击伤害：</span>
                <span style="text-align: right">
                  <span> {{ userInfo.userAttr.crtd }}% </span>
                </span>
              </p>
              <p>
                <span>破防：</span><span>{{ userInfo.userAttr.heat }}</span>
              </p>
              <p>
                <span>命中率：</span><span>{{ userInfo.userAttr.hr }}%</span>
              </p>
              <p>
                <span>抗魔值：</span><span>{{ formatNumber(userInfo.userAttr.voidDef, 2) }}</span>
              </p>
              <p>
                <span>全伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.ad, 2) }}%</span>
              </p>

              <p>
                <span>魔法值：</span><span>{{ userInfo.userAttr.mp }}</span>
              </p>
              <p>
                <span>魔法回复：</span><span>{{ userInfo.userAttr.mpRegen }}</span>
              </p>
              <p>
                <span>冷却缩减：</span>
                <span style="text-align: right">
                  <span>{{ formatNumber(userInfo.userAttr.cda, 2) }} ({{ formatNumber(getRealSpecAttr(userInfo.userAttr.cda), 2) }}%)</span>
                </span>
              </p>
              <p>
                <span>法耗缩减：</span>
                <span style="text-align: right">
                  <span>{{ formatNumber(userInfo.userAttr.sce, 2) }} ({{ formatNumber(getRealSpecAttr(userInfo.userAttr.sce), 2) }}%)</span>
                </span>
              </p>
              <p>
                <span>技能伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.skillDa, 2) }}%</span>
              </p>
              <p>
                <span>风伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.windDa, 2) }}%</span>
              </p>
              <p>
                <span>火伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.fireDa, 2) }}%</span>
              </p>
              <p>
                <span>水伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.waterDa, 2) }}%</span>
              </p>
              <p>
                <span>土伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.soilDa, 2) }}%</span>
              </p>
              <el-popover placement="bottom" width="50vw" popper-class="see-attr" trigger="click">
                <template #reference>
                  <el-button type="primary" plain size="small" style="width: 100%">更多属性</el-button>
                </template>
                <div class="border-wrap" style="padding: 0 10px; max-width: 50vh; max-height: 30vh; overflow: auto">
                  <p>
                    <span>独立风伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.windIndDa) }}%</span>
                  </p>
                  <p>
                    <span>独立火伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.fireIndDa) }}%</span>
                  </p>
                  <p>
                    <span>独立水伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.waterIndDa) }}%</span>
                  </p>
                  <p>
                    <span>独立土伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.soilIndDa) }}%</span>
                  </p>
                  <p>
                    <span>全技能等级加成：</span><span>{{ userInfo.userAttr.allSkillGrow }}</span>
                  </p>
                  <p>
                    <span>风系技能等级加成：</span><span>{{ userInfo.userAttr.windSkillGrow }}</span>
                  </p>
                  <p>
                    <span>火系技能等级加成：</span><span>{{ userInfo.userAttr.fireSkillGrow }}</span>
                  </p>
                  <p>
                    <span>水系技能等级加成：</span><span>{{ userInfo.userAttr.waterSkillGrow }}</span>
                  </p>
                  <p>
                    <span>土系技能等级加成：</span><span>{{ userInfo.userAttr.soilSkillGrow }}</span>
                  </p>
                  <p>
                    <span>对不死族伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.undeadDa, 2) }}%</span>
                  </p>
                  <p>
                    <span>对兽族伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.orcDa, 2) }}%</span>
                  </p>
                  <p>
                    <span>对穴居族伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.caveDa, 2) }}%</span>
                  </p>
                  <p>
                    <span>对元素族伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.elDa, 2) }}%</span>
                  </p>
                  <p>
                    <span>对精灵伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.elfDa, 2) }}%</span>
                  </p>
                  <p>
                    <span>对恶魔伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.dmDa, 2) }}%</span>
                  </p>
                  <p>
                    <span>对妖精伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.faeDa, 2) }}%</span>
                  </p>
                  <p>
                    <span>对泥泽族伤害加成：</span><span>{{ formatNumber(userInfo.userAttr.mzDa, 2) }}</span>
                  </p>
                </div>
              </el-popover>
            </div>
          </template>
        </div>
      </div>
      <div class="border-wrap equip-list">
        <h4>
          <span :style="{ opacity: showType === 'equip' ? '1' : '0.5' }" @click="showType = 'equip'">装备</span>
          <span> | </span>
          <span :style="{ opacity: showType === 'rune' ? '1' : '0.5' }" @click="showType = 'rune'">符石</span>
          <span> | </span>
          <span :style="{ opacity: showType === 'relic' ? '1' : '0.5' }" @click="showType = 'relic'">圣物</span>
          <span> | </span>
          <span :style="{ opacity: showType === 'skill' ? '1' : '0.5' }" @click="showType = 'skill'">技能</span>
        </h4>
        <p style="text-align: center" v-show="!equipInfoList">（已隐藏）</p>
        <div class="btn-wrap item-btn-wrap" v-show="showType === 'equip'">
          <el-popover
            v-for="(item, index) of equipInfoList"
            placement="left"
            width="50%"
            popper-style="max-width: 350px"
            trigger="click"
            :disabled="!item"
            :teleported="false"
          >
            <template #reference>
              <CommonBtn :key="index" size="small" :type="item ? 'primary' : 'info'">
                <span v-if="item" v-html="getEquipName(item, item.type)"></span>
                <span v-else>{{ equipIcon[equipTypeList[index]] }} (未装备)</span>
              </CommonBtn>
            </template>
            <template v-if="item">
              <EquipInfo :equip-info="item" :show-action="false" :show-extra="false" style="max-height: 70vh" />
            </template>
          </el-popover>
        </div>
        <div class="btn-wrap item-btn-wrap" v-show="showType === 'rune'">
          <el-popover
            v-for="(item, index) of props.userInfo.runeList"
            placement="left"
            width="50%"
            popper-style="max-width: 350px"
            trigger="click"
            :disabled="!item"
          >
            <template #reference>
              <CommonBtn :key="index" size="small" :type="item ? 'primary' : 'info'">
                <span v-if="item" :class="item.quality" v-html="getRuneName(item.runeId)"></span>
                <span v-else>（未装备）</span>
              </CommonBtn>
            </template>
            <template v-if="item">
              <RuneInfo :rune-info="item" :show-action="false" :InItems="false" />
            </template>
          </el-popover>
        </div>
        <div class="btn-wrap item-btn-wrap" v-show="showType === 'relic'">
          <el-popover
            v-for="(item, index) of props.userInfo.relicList"
            placement="left"
            width="50%"
            popper-style="max-width: 350px"
            trigger="click"
            :disabled="!item"
          >
            <template #reference>
              <CommonBtn :key="index" size="small" :type="item ? 'primary' : 'info'">
                <span v-if="item" :class="item.quality" v-html="getRelicName(item.relicId, item.count)"></span>
                <span v-else>（未装备）</span>
              </CommonBtn>
            </template>
            <template v-if="item">
              <RelicInfo :relic-info="item" :show-action="false" :InItems="false" />
            </template>
          </el-popover>
        </div>
        <div class="btn-wrap item-btn-wrap" v-show="showType === 'skill'">
          <el-popover
            v-for="(item, index) of props.userInfo.skillList"
            placement="left"
            width="50%"
            popper-style="max-width: 350px"
            trigger="click"
            :disabled="!item"
          >
            <template #reference>
              <CommonBtn :key="index" size="small" :type="item ? 'primary' : 'info'">
                <span v-if="item" :class="item.quality" v-html="getSkillName(item, false, 0)"></span>
                <span v-else>（未携带）</span>
              </CommonBtn>
            </template>
            <template v-if="item">
              <SkillInfo :skill-info="item" :show-action="false" />
            </template>
          </el-popover>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.other-user-info {
  overflow: hidden;
  .info {
    width: 100%;
    display: flex;
    justify-content: space-between;
    overflow: hidden;
    & > div {
      width: calc(50% - 2.5px);
      flex-shrink: 0;
    }
    h4 {
      width: 100%;
      display: inline-block;
      font-size: 14px;
      text-align: center;
      margin: 10px 0;
    }
  }
  .border-wrap {
    padding: 0 5px 5px 5px;
  }
  .player-info {
    max-height: 100%;
    overflow: hidden;
    p {
      display: flex;
      justify-content: space-between;
      margin: 14px 0;
    }
    .attr-info {
      max-height: calc(100% - 48px);
      overflow: auto;
    }
  }
  .text-wrap {
    max-height: 50vh;
    overflow: auto;

    p {
      font-size: 14px;
      margin: 4px 0;
      display: flex;
      justify-content: space-between;
      gap: 8px;
    }
  }

  :deep() {
    .el-button:focus,
    .el-button:hover {
      background-color: unset;
    }
  }
  .btn-wrap {
    & > div {
      margin-bottom: 10px;
    }
  }
}
</style>
